import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../AuthWrapper';
import { useLanguage } from '../../LanguageContext';
import useAllTasksSeries from '../../hooks/useAllTasksSeries';
import { ALL_TASKS_TRAVERSAL_ENABLED } from '../../counsellorModes';
import useFullSessionReportState from '../../hooks/useFullSessionReportState';
import useWorkingAllianceSeries from '../../hooks/useWorkingAllianceSeries';
import CreditsBadge from './CreditsBadge';
import ReportHamburgerMenu from './ReportHamburgerMenu';
import ReportFullSessionAnalytics from './ReportFullSessionAnalytics';
import ReportOarsPanel, { OarsActionDock } from './ReportOarsPanel';
import ReportSpecificStrategiesPanel from './ReportSpecificStrategiesPanel';
import ReportFullSessionPostSessionPanel from './ReportFullSessionPostSessionPanel';
import Ts3TraversalPanel from './Ts3TraversalPanel';
import ReportTurnNavigator from './ReportTurnNavigator';
import WorkingAllianceChart from './WorkingAllianceChart';
import WorkingAllianceIntroNotice from './WorkingAllianceIntroNotice';
import SkillCodeNameTooltip from './SkillCodeNameTooltip';
import '../dialogs/DialogBase.css';
import '../dialogs/FeedbackDialog.css';
import './ReportPanel.css';

const MITI_CODE_CLASS = {
  Q: 'miti-chip--Q', OQ: 'miti-chip--Q', CQ: 'miti-chip--Q', S: 'miti-chip--S', AF: 'miti-chip--AF', SR: 'miti-chip--SR', CR: 'miti-chip--CR',
  Seek: 'miti-chip--Seek', GI: 'miti-chip--GI', Persuade: 'miti-chip--Persuade',
  Persuade_with: 'miti-chip--Persuade_with', Emphasize: 'miti-chip--Emphasize',
  Confront: 'miti-chip--Confront', NC: 'miti-chip--NC',
};

const MITI_DISPLAY_NAMES = {
  SR: 'Simple Reflection',
  CR: 'Complex Reflection',
  OQ: 'Open-Ended Question',
  CQ: 'Closed Question',
  Q: 'Question',
  S: 'Summary',
  AF: 'Affirmation',
  GI: 'Giving Information',
  Seek: 'Seeking Collaboration',
  Persuade: 'Persuade',
  Persuade_with: 'Persuade with Permission',
  Emphasize: 'Emphasize Autonomy',
  Confront: 'Confront',
  NC: 'Not Coded',
};

const EMPTY_TURN_MESSAGES = Object.freeze({ counsellor: [], client: [] });
const WAI_INTRO_DISMISSED_KEY = 'aimie_wai_intro_dismissed_v1';

function LoadingDots() {
  return (
    <span className="loading-dots">
      <span /><span /><span />
    </span>
  );
}

function MitiChip({ label, matched, expand = false, compactTooltip = false }) {
  const codeClass = MITI_CODE_CLASS[label] ?? 'miti-chip--NC';
  const fullName = MITI_DISPLAY_NAMES[label] ?? label;
  const display = expand ? fullName : label;
  const indicator = matched === true ? ' ✓' : matched === false ? ' ✗' : '';
  const chip = (
    <span
      className={`miti-chip ${codeClass}`}
      aria-label={!expand && fullName !== label ? fullName : undefined}
    >
      {display}{indicator}
    </span>
  );
  if (compactTooltip && !expand && fullName !== label) {
    return <SkillCodeNameTooltip name={fullName}>{chip}</SkillCodeNameTooltip>;
  }
  return chip;
}

// Renders a counsellor system message: if it's a MITI line, keep the prefix as text
// and replace only the comma-separated codes with coloured chips.
function AnalyticsMessage({ msg }) {
  const MITI_PREFIX = "Counsellor's MITI: ";
  if (msg.startsWith(MITI_PREFIX)) {
    const codes = msg.slice(MITI_PREFIX.length).split(',').map(s => s.trim()).filter(Boolean);
    return (
      <div className="analytics-message">
        <span className="analytics-bullet">•</span>
        <span className="analytics-text">
          {MITI_PREFIX}
          {codes.map(code => <MitiChip key={code} label={code} />)}
        </span>
      </div>
    );
  }
  return (
    <div className="analytics-message">
      <span className="analytics-bullet">•</span>
      <span className="analytics-text">{msg}</span>
    </div>
  );
}

function ReportPanel({
  sessionId,
  sessionActive,
  conversationStarted,
  fullSessionVignette = null,
  historicalReplay = null,
  clientModeCounsellorMode = null,
  systemMessagesHistory,
  viewedTurnIndex,
  onViewedTurnChange,
  onHighlightedTurnChange,
  revealedItems,
  stageMetrics,
  onRestartSession,
  onFeedbackPdfDownloaded,
  onFeedbackReportReadyChange,
  onCompactPostSessionStatusChange,
  onCompactSessionObserverStatusChange,
  onCompactTurnNavigatorStatusChange,
  onHistoricalDownloadPdf,
  onHistoricalDownloadCsv,
  openDownloadsInNewTab = false,
  onReturnToAccount,
  menuOpen,
  toggleMenu,
  closeMenu,
  menuRef,
  menuPortalRef,
  handleThemeChange,
  theme,
  collapsed,
  onToggleCollapse,
  onShowContacts,
  onShowAccount,
  onShowAbout,
  onShowWorkingAlliance,
  onShowLectures,
  onShowTutorial,
  onShowAutomatedMiscMiti,
  onShowAutomatedMiscMitiCounsellor,
  onShowSimpleReflections,
  onShowAffirmations,
  onShowComplexReflections,
  onShowClient,
  onShowCounsellors,
  showNotification,
  showPostDownloadNextStepCue = false,
  disableFullSessionCues = false,
  suppressTransientNotices = false,
  showSpecificSkillsPostAttemptCue = false,
  guidedHighlightsEnabled = true,
  onGuidedHighlightsChange,
  skillsAnnotationsEnabled = true,
  onSkillsAnnotationsChange,
  suggestionsEnabled = true,
  onSuggestionsChange,
  isClientMode = false,
  isOarsMode = false,
  isStrategiesMode = false,
  strategiesFlow = null,
  isSpecificStrategyReplay = false,
  onReturnToAccountFromSpecificStrategyReplay,
  onSpecificStrategyTryAgain,
  isLlmModeSession = false,
  sessionCounsellorMode = null,
  preferredAnalyticsVisible = true,
  onAnalyticsVisibleChange,
  oarsExercise = null,
  oarsAttempt = null,
  oarsAnalyzing = false,
  oarsStreamingEvaluation = null,
  oarsFilter = undefined,
  onOarsTryAgain,
  onOarsNextExercise,
  isSpecificSkillReplay = false,
  onReturnToAccountFromSpecificSkillReplay,
  language = 'en',
  onLanguageChange,
  streamingActive = false,
  isSingleColumnViewport = false,
  onFullSessionGuidanceVisibleChange,
  onSignOut,
}) {
  const { t } = useLanguage();
  const isHistoricalReplay = Boolean(historicalReplay);
  const { requireAuth, authenticated } = useAuth();
  const notify = showNotification || ((message) => window.alert(message));
  const [btcTs3Panels, setBtcTs3Panels] = useState({
    ts3: true,
    wai: true,
    analytics: false,
  });
  const [workingAllianceCollapsed, setWorkingAllianceCollapsed] = useState(true);
  const [workingAllianceChartHeight, setWorkingAllianceChartHeight] = useState(null);
  const waiAutoOpenedSessionRef = useRef(null);
  const waiHasAutoOpenedRef = useRef(false);
  const waiSectionRef = useRef(null);
  const waiIntroShownRef = useRef(false);
  const [waiIntroOpen, setWaiIntroOpen] = useState(false);
  const [waiIntroDismissed, setWaiIntroDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(WAI_INTRO_DISMISSED_KEY) === 'true';
    } catch (_error) {
      return false;
    }
  });
  const fullSessionStreamingCycleRef = useRef({ active: false, turnMaterialized: false });
  const [downloadNoticeOpen, setDownloadNoticeOpen] = useState(false);
  const [strategiesCompletionCueVisible, setStrategiesCompletionCueVisible] = useState(false);
  const [fullSessionGuidanceVisible, setFullSessionGuidanceVisible] = useState(false);
  // Stable handlers for the single-column drawer, whose status objects are
  // published from effects (per-render closures would republish every render).
  const revealFullSessionGuidance = useCallback(() => setFullSessionGuidanceVisible(true), []);
  const hideFullSessionGuidance = useCallback(() => setFullSessionGuidanceVisible(false), []);
  const codingManualRef = useRef(onShowAutomatedMiscMitiCounsellor);
  useEffect(() => {
    codingManualRef.current = onShowAutomatedMiscMitiCounsellor;
  });
  const openCodingManualFromDrawer = useCallback(() => codingManualRef.current?.(), []);
  const drawerCodingManualHandler = onShowAutomatedMiscMitiCounsellor ? openCodingManualFromDrawer : undefined;
  const reportSessionId = historicalReplay?.id || null;
  const fullSessionGuidanceSessionKey = historicalReplay?.id || sessionId || null;

  useEffect(() => {
    setFullSessionGuidanceVisible(false);
  }, [fullSessionGuidanceSessionKey]);

  useEffect(() => {
    onFullSessionGuidanceVisibleChange?.(fullSessionGuidanceVisible);
  }, [fullSessionGuidanceVisible, onFullSessionGuidanceVisibleChange]);
  const downloadNoticeSessionsRef = useRef(new Set());
  const {
    analysisStatus,
    analyticsVisible,
    postSessionView,
    setPostSessionView,
    toggleAnalyticsVisibility,
    handleDownloadPdf,
    handleDownloadCsv,
  } = useFullSessionReportState({
    sessionId,
    sessionActive,
    conversationStarted,
    isOarsMode,
    isLlmModeSession,
    historicalAnalysisStatus: historicalReplay?.analysis_status || null,
    onHistoricalDownloadPdf: historicalReplay
      ? (options) => onHistoricalDownloadPdf?.(historicalReplay.id, options)
      : null,
    onHistoricalDownloadCsv: historicalReplay
      ? (options) => onHistoricalDownloadCsv?.(historicalReplay.id, options)
      : null,
    preferredAnalyticsVisible,
    onAnalyticsVisibleChange,
    onViewedTurnChange,
    onFeedbackPdfDownloaded,
    showNotification: notify,
    t,
    openDownloadsInNewTab,
  });
  useEffect(() => {
    if (!onFeedbackReportReadyChange) {
      return;
    }

    if (isHistoricalReplay || isClientMode || isOarsMode) {
      onFeedbackReportReadyChange(false);
      return;
    }

    onFeedbackReportReadyChange(Boolean(analysisStatus?.pdf_complete));
  }, [
    analysisStatus?.pdf_complete,
    isClientMode,
    isHistoricalReplay,
    isOarsMode,
    onFeedbackReportReadyChange,
  ]);

  // Turn navigation logic
  const baseTurns = systemMessagesHistory?.length || 0;
  const isOnLatestTurn = viewedTurnIndex === null || viewedTurnIndex === baseTurns - 1;
  const hasProvisionalFullSessionTurn = Boolean(
    baseTurns > 0 && systemMessagesHistory[baseTurns - 1]?.fullSessionProvisional
  );
  if (!streamingActive) {
    fullSessionStreamingCycleRef.current.active = false;
    fullSessionStreamingCycleRef.current.turnMaterialized = false;
  } else {
    if (!fullSessionStreamingCycleRef.current.active) {
      fullSessionStreamingCycleRef.current.active = true;
      fullSessionStreamingCycleRef.current.turnMaterialized = false;
    }
    if (hasProvisionalFullSessionTurn) {
      fullSessionStreamingCycleRef.current.turnMaterialized = true;
    }
  }
  const showStreamingTurn = (
    streamingActive
    && isOnLatestTurn
    && !hasProvisionalFullSessionTurn
    && !fullSessionStreamingCycleRef.current.turnMaterialized
  );
  const totalTurns = showStreamingTurn ? baseTurns + 1 : baseTurns;
  const effectiveTurnIndex = showStreamingTurn
    ? totalTurns - 1
    : (viewedTurnIndex === null ? totalTurns - 1 : viewedTurnIndex);
  const isViewingLatest = showStreamingTurn || viewedTurnIndex === null || viewedTurnIndex === totalTurns - 1;
  const currentTurnMessages = totalTurns > 0 && effectiveTurnIndex >= 0
    ? systemMessagesHistory[effectiveTurnIndex]
    : EMPTY_TURN_MESSAGES;
  const visibleWorkingAllianceSeries = useWorkingAllianceSeries({
    systemMessagesHistory,
    viewedTurnIndex,
  });
  const showLiveSessionObservers = !isHistoricalReplay && !!sessionId && sessionActive;

  useEffect(() => {
    const waiSessionKey = isHistoricalReplay
      ? `history-${historicalReplay?.session_id || historicalReplay?.id || 'session'}`
      : `live-${sessionId || 'none'}`;

    if (waiAutoOpenedSessionRef.current !== waiSessionKey) {
      waiAutoOpenedSessionRef.current = waiSessionKey;
      waiHasAutoOpenedRef.current = false;
      setWorkingAllianceCollapsed(true);
      setWorkingAllianceChartHeight(null);
    }
  }, [historicalReplay, isHistoricalReplay, sessionId]);

  useEffect(() => {
    if (
      visibleWorkingAllianceSeries.series.length > 0
      && !waiHasAutoOpenedRef.current
    ) {
      waiHasAutoOpenedRef.current = true;
      setWorkingAllianceCollapsed(false);
    }
    // Auto-open only once per session. A user's later manual collapse remains
    // respected even as additional WAI points arrive.
  }, [visibleWorkingAllianceSeries.series.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      waiIntroDismissed
      || waiIntroShownRef.current
      || isSingleColumnViewport
      || !showLiveSessionObservers
      || visibleWorkingAllianceSeries.series.length === 0
    ) {
      return;
    }

    waiIntroShownRef.current = true;
    setWaiIntroOpen(true);
  }, [
    isSingleColumnViewport,
    showLiveSessionObservers,
    visibleWorkingAllianceSeries.series.length,
    waiIntroDismissed,
  ]);

  const handleWaiIntroDismissedChange = (dismissed) => {
    setWaiIntroDismissed(dismissed);
    try {
      if (dismissed) {
        window.localStorage.setItem(WAI_INTRO_DISMISSED_KEY, 'true');
      } else {
        window.localStorage.removeItem(WAI_INTRO_DISMISSED_KEY);
      }
    } catch (_error) {
      // Browser privacy settings may make localStorage unavailable.
    }
  };
  const visibleAllTasksSeries = useAllTasksSeries({
    systemMessagesHistory,
    viewedTurnIndex,
  });
  const displayStageMetrics = (!isViewingLatest && currentTurnMessages?.stageMetrics)
    ? currentTurnMessages.stageMetrics
    : stageMetrics;
  const hasStageMetricsData = Boolean(
    displayStageMetrics &&
    (
      displayStageMetrics.stage !== null ||
      displayStageMetrics.change_score !== null ||
      displayStageMetrics.change_score_floor !== null ||
      displayStageMetrics.refusal_count !== null
    )
  );
  const showBtcStage1ReadyState = !isHistoricalReplay && isClientMode && !sessionId;
  const showBtcStage2ReadyState = !isHistoricalReplay && isClientMode && !!sessionId && !conversationStarted;
  const showStrategiesReadyState = !isHistoricalReplay && isStrategiesMode && !strategiesFlow?.exercise;
  const showFreeformStage1ReadyState = !isHistoricalReplay && !isStrategiesMode && !isOarsMode && !isClientMode && !sessionActive && !sessionId;
  const showFreeformStage2ReadyState = !isHistoricalReplay && !isStrategiesMode && !isOarsMode && !isClientMode && sessionActive && !conversationStarted;
  const reportWriting = Boolean(analysisStatus?.metrics && !analysisStatus?.pdf_complete);
  const showDownloadCue = !disableFullSessionCues && !isHistoricalReplay && !isClientMode && analysisStatus && (reportWriting || (analysisStatus.pdf_complete && !showPostDownloadNextStepCue));
  const showDownloadNotice = suggestionsEnabled && downloadNoticeOpen && !suppressTransientNotices;
  const showReturnToAccountCue = !disableFullSessionCues && isHistoricalReplay && showPostDownloadNextStepCue;
  const liveMetricsActionsEnabled = isClientMode
    ? analysisStatus?.status === 'completed'
    : (analysisStatus?.status === 'completed' || analysisStatus?.pdf_complete);

  const showOarsActionDock = isOarsMode && !!oarsExercise && !!oarsAttempt && !oarsAnalyzing;
  const strategyStimulusTurnCount = strategiesFlow?.exercise?.turn_count || 0;
  const showStrategiesActionDock = (
    isStrategiesMode &&
    Boolean(strategiesFlow?.exercise) &&
    (strategiesFlow?.conversation?.length || 0) > strategyStimulusTurnCount
  );

  useEffect(() => {
    setStrategiesCompletionCueVisible(false);
    if (!isStrategiesMode || strategiesFlow?.status !== 'complete') return undefined;

    const timeout = window.setTimeout(() => {
      setStrategiesCompletionCueVisible(true);
    }, 2000);
    return () => window.clearTimeout(timeout);
  }, [isStrategiesMode, strategiesFlow?.attemptId, strategiesFlow?.status]);
  const hasPostSessionReplayContext = isHistoricalReplay || (!!sessionId && !sessionActive && !!analysisStatus);
  const showPostSessionAnalyticsPanel = !isOarsMode && hasPostSessionReplayContext && postSessionView === 'analytics';
  const showPostSessionMetricsPanel = !isOarsMode && hasPostSessionReplayContext && postSessionView === 'metrics';
  const postSessionMetricsBodyVisible = showPostSessionMetricsPanel && Boolean(
    isClientMode
      ? analysisStatus?.status === 'completed'
      : analysisStatus?.metrics
  );
  const canReviewPostSessionAnalytics = Array.isArray(systemMessagesHistory) && systemMessagesHistory.length > 0;
  const showLiveMetricsActionDock = (
    !isHistoricalReplay &&
    postSessionMetricsBodyVisible
  );
  const showHistoricalMetricsActionDock = (
    isHistoricalReplay &&
    postSessionView === 'metrics' &&
    canReviewPostSessionAnalytics
  );
  const hasAllTasksSeries = visibleAllTasksSeries.series.length > 0;
  const downloadNoticeSessionKey = `live-${sessionId}`;

  useEffect(() => {
    if (sessionActive || isHistoricalReplay) {
      setDownloadNoticeOpen(false);
      return;
    }

    if (
      !suggestionsEnabled ||
      isClientMode ||
      isOarsMode ||
      !sessionId ||
      !analysisStatus?.pdf_complete ||
      downloadNoticeSessionsRef.current.has(downloadNoticeSessionKey)
    ) {
      return;
    }

    downloadNoticeSessionsRef.current.add(downloadNoticeSessionKey);
    setDownloadNoticeOpen(true);
  }, [
    analysisStatus?.pdf_complete,
    isClientMode,
    isHistoricalReplay,
    isOarsMode,
    sessionActive,
    sessionId,
    suggestionsEnabled,
    downloadNoticeSessionKey,
  ]);
  const showLiveTs3TraversalPanel = (
    showLiveSessionObservers &&
    (
      (isClientMode && clientModeCounsellorMode === 'all-tasks') ||
      (!isClientMode && isLlmModeSession && sessionCounsellorMode === 'all-tasks')
    ) &&
    conversationStarted
  );
  const showReplayTs3TraversalPanel = (
    !isOarsMode &&
    conversationStarted &&
    hasAllTasksSeries &&
    (
      showPostSessionAnalyticsPanel ||
      (isHistoricalReplay && postSessionView === 'analytics')
    )
  );
  const showTs3TraversalPanel = ALL_TASKS_TRAVERSAL_ENABLED && (
    showLiveTs3TraversalPanel || showReplayTs3TraversalPanel
  );
  const showWorkingAllianceChart = (
    !isOarsMode &&
    conversationStarted &&
    (
      showLiveSessionObservers ||
      (
        (!sessionActive || isHistoricalReplay) &&
        postSessionView === 'analytics'
      )
    )
  );

  // Declared after showWorkingAllianceChart: the single-column drawer mirrors
  // the same post-session observers the 3-column panel shows.
  useEffect(() => {
    if (!onCompactPostSessionStatusChange) {
      return undefined;
    }

    if (isOarsMode || (!isHistoricalReplay && (!sessionId || sessionActive)) || !analysisStatus) {
      onCompactPostSessionStatusChange(null);
      return undefined;
    }

    onCompactPostSessionStatusChange({
      analysisStatus,
      theme,
      isClientMode,
      postSessionView,
      currentTurnMessages,
      displayStageMetrics,
      hasStageMetricsData,
      revealedItems,
      showWorkingAlliance: showWorkingAllianceChart,
      workingAllianceSeries: visibleWorkingAllianceSeries.series,
      workingAllianceVisibleCount: visibleWorkingAllianceSeries.visibleCount,
      onBackToMetrics: () => {
        setPostSessionView('metrics');
        onViewedTurnChange(null);
      },
      showDownloadCue,
      showDownloadNotice,
      onCloseDownloadNotice: () => setDownloadNoticeOpen(false),
      onDownloadPdf: handleDownloadPdf,
      onDownloadCsv: handleDownloadCsv,
      showHistoricalReplayActions: isHistoricalReplay,
      showLiveSessionActions: !isHistoricalReplay,
      liveMetricsActionsEnabled,
      canReviewAnalytics: Array.isArray(systemMessagesHistory) && systemMessagesHistory.length > 0,
      onReviewAnalytics: () => setPostSessionView('analytics'),
      onRestartSession,
      showStartNewSessionCue: !disableFullSessionCues && !isHistoricalReplay && showPostDownloadNextStepCue,
      onReturnToAccount,
      showReturnToAccountCue,
      turnIndex: effectiveTurnIndex,
      reportSessionId,
      guidanceVisible: fullSessionGuidanceVisible,
      onRevealGuidance: revealFullSessionGuidance,
      onHideGuidance: hideFullSessionGuidance,
      onShowAutomatedMiscMitiCounsellor: drawerCodingManualHandler,
    });

    return () => onCompactPostSessionStatusChange(null);
  }, [
    analysisStatus,
    theme,
    effectiveTurnIndex,
    reportSessionId,
    fullSessionGuidanceVisible,
    revealFullSessionGuidance,
    hideFullSessionGuidance,
    drawerCodingManualHandler,
    currentTurnMessages,
    displayStageMetrics,
    hasStageMetricsData,
    postSessionView,
    revealedItems,
    showWorkingAllianceChart,
    visibleWorkingAllianceSeries.series,
    visibleWorkingAllianceSeries.visibleCount,
    onViewedTurnChange,
    handleDownloadCsv,
    handleDownloadPdf,
    isClientMode,
    liveMetricsActionsEnabled,
    isHistoricalReplay,
    isOarsMode,
    onRestartSession,
    onCompactPostSessionStatusChange,
    onReturnToAccount,
    sessionId,
    sessionActive,
    setPostSessionView,
    disableFullSessionCues,
    showPostDownloadNextStepCue,
    showDownloadCue,
    showDownloadNotice,
    showReturnToAccountCue,
    systemMessagesHistory,
  ]);

  useEffect(() => {
    if (showTs3TraversalPanel) {
      setBtcTs3Panels({ ts3: true, wai: true, analytics: false });
    }
  }, [showTs3TraversalPanel, sessionId]);

  useEffect(() => {
    if (!showTs3TraversalPanel) {
      return;
    }

    if (!btcTs3Panels.analytics) {
      return;
    }

    setBtcTs3Panels((current) => {
      if (!current.analytics || (!current.ts3 && !current.wai)) {
        return current;
      }
      return { ts3: false, wai: false, analytics: true };
    });
  }, [btcTs3Panels, showTs3TraversalPanel]);

  const toggleBtcTs3Panel = (panelName) => {
    setBtcTs3Panels((current) => {
      const opening = !current[panelName];
      if (!opening) {
        return { ...current, [panelName]: false };
      }

      if (panelName === 'analytics') {
        return { ts3: false, wai: false, analytics: true };
      }

      return { ...current, [panelName]: true, analytics: false };
    });
  };
  const effectiveAnalyticsVisible = showTs3TraversalPanel ? btcTs3Panels.analytics : analyticsVisible;
  const regularWorkingAllianceCollapsed = !showTs3TraversalPanel && workingAllianceCollapsed;
  // Live in-session analytics and the post-session review share one layout, so
  // their headers sit in the same place.
  const fullSessionAnalyticsShown = (
    (!isOarsMode && sessionActive && conversationStarted)
    || showPostSessionAnalyticsPanel
  );
  const regularLiveAnalyticsCollapsed = (
    !showTs3TraversalPanel
    && !effectiveAnalyticsVisible
    && fullSessionAnalyticsShown
  );
  // Only offer the drag handle when WAI and the analytics below it are both open.
  const canResizeWorkingAlliance = (
    !showTs3TraversalPanel
    && !workingAllianceCollapsed
    && !regularLiveAnalyticsCollapsed
  );
  const handleAnalyticsToggle = showTs3TraversalPanel
    ? () => toggleBtcTs3Panel('analytics')
    : toggleAnalyticsVisibility;
  const effectiveWorkingAllianceCollapsed = showTs3TraversalPanel ? !btcTs3Panels.wai : workingAllianceCollapsed;
  const handleWorkingAllianceToggle = showTs3TraversalPanel
    ? () => toggleBtcTs3Panel('wai')
    : () => setWorkingAllianceCollapsed((value) => !value);

  // The single-column drawer drives this panel's own WAI/analytics state so it
  // behaves exactly like the 3-column report. Its status object is published
  // from an effect, so hand it stable wrappers rather than per-render closures.
  const observerTogglesRef = useRef({});
  useEffect(() => {
    observerTogglesRef.current = {
      analytics: handleAnalyticsToggle,
      workingAlliance: handleWorkingAllianceToggle,
    };
  });
  const toggleAnalyticsFromDrawer = useCallback(() => observerTogglesRef.current.analytics?.(), []);
  const toggleWorkingAllianceFromDrawer = useCallback(() => observerTogglesRef.current.workingAlliance?.(), []);

  useEffect(() => {
    if (!onCompactSessionObserverStatusChange) {
      return undefined;
    }

    if (!showLiveSessionObservers || !conversationStarted || isOarsMode) {
      onCompactSessionObserverStatusChange(null);
      return undefined;
    }

    onCompactSessionObserverStatusChange({
      currentTurnMessages,
      displayStageMetrics,
      hasStageMetricsData,
      streamingActive,
      isViewingLatest,
      isClientMode,
      conversationStarted,
      revealedItems,
      analyticsVisible: effectiveAnalyticsVisible,
      onToggleAnalytics: toggleAnalyticsFromDrawer,
      showWorkingAlliance: showWorkingAllianceChart,
      workingAllianceCollapsed: effectiveWorkingAllianceCollapsed,
      onToggleWorkingAlliance: toggleWorkingAllianceFromDrawer,
      workingAllianceSeries: visibleWorkingAllianceSeries.series,
      workingAllianceVisibleCount: visibleWorkingAllianceSeries.visibleCount,
      theme,
      turnIndex: effectiveTurnIndex,
      reportSessionId,
      guidanceVisible: fullSessionGuidanceVisible,
      onRevealGuidance: revealFullSessionGuidance,
      onHideGuidance: hideFullSessionGuidance,
      onShowAutomatedMiscMitiCounsellor: drawerCodingManualHandler,
    });

    return () => onCompactSessionObserverStatusChange(null);
  }, [
    theme,
    effectiveTurnIndex,
    reportSessionId,
    fullSessionGuidanceVisible,
    revealFullSessionGuidance,
    hideFullSessionGuidance,
    drawerCodingManualHandler,
    conversationStarted,
    currentTurnMessages,
    displayStageMetrics,
    effectiveAnalyticsVisible,
    effectiveWorkingAllianceCollapsed,
    hasStageMetricsData,
    isClientMode,
    isOarsMode,
    isViewingLatest,
    onCompactSessionObserverStatusChange,
    revealedItems,
    showLiveSessionObservers,
    showWorkingAllianceChart,
    streamingActive,
    toggleAnalyticsFromDrawer,
    toggleWorkingAllianceFromDrawer,
    visibleWorkingAllianceSeries.series,
    visibleWorkingAllianceSeries.visibleCount,
  ]);

  const ts3HasRoomFromClosedAnalytics = showTs3TraversalPanel && btcTs3Panels.ts3 && !effectiveAnalyticsVisible;
  const ts3HasRoomFromClosedWai = showTs3TraversalPanel && btcTs3Panels.ts3 && showWorkingAllianceChart && !btcTs3Panels.wai;
  const ts3OwnsAvailableSpace = showTs3TraversalPanel && btcTs3Panels.ts3 && !effectiveAnalyticsVisible;
  const clientModeCounsellorConfig = clientModeCounsellorMode === 'volley'
    ? {
        label: t('chat_counsellor_freeform'),
        body: 'A fully generative counsellor that usually feels the most natural and conversational, without a separate strategy selector choosing each move first. It reads the exchange and produces the next response directly, so its guidance is more implicit and its turns often feel smoother and less visibly structured.',
      }
    : clientModeCounsellorMode === 'utterance_multiBC'
      ? {
          label: t('chat_counsellor_technique'),
          body: 'A counsellor that selects one or two MI strategies before each response, making the conversation more deliberate while staying focused on the immediate exchange. It still writes fluent responses, but each turn is shaped by an explicit planning step that helps steer the local interaction toward a specific MI-consistent move.',
        }
      : clientModeCounsellorMode === 'stage_guided_multiBC'
        ? {
            label: t('chat_counsellor_stage'),
            body: 'A more structured counsellor that uses session stage and change-talk context to choose responses, not just the last utterance. It is designed to think more strategically about where the conversation is headed overall, so its turns are guided by both the current moment and the broader progression of the session.',
          }
        : null;
  // The turn whose snapshot first contains this series entry.
  const findTurnForTs3Entry = (entryIndex) => {
    if (!Array.isArray(systemMessagesHistory) || !systemMessagesHistory.length) return -1;
    return systemMessagesHistory.findIndex(
      (turn) => (turn?.allTasksSeries?.length || 0) >= entryIndex + 1
    );
  };

  const handleTs3EntrySelect = (entryIndex) => {
    const targetTurn = findTurnForTs3Entry(entryIndex);
    if (targetTurn === -1) return;
    onViewedTurnChange(targetTurn >= baseTurns - 1 ? null : targetTurn);
  };

  const handleTs3EntryHover = (entryIndex) => {
    if (!onHighlightedTurnChange) return;
    if (entryIndex === null || entryIndex === undefined) {
      onHighlightedTurnChange(null);
      return;
    }
    const targetTurn = findTurnForTs3Entry(entryIndex);
    onHighlightedTurnChange(targetTurn === -1 ? null : targetTurn);
  };

  const handlePrevTurn = () => {
    if (effectiveTurnIndex > 0) {
      onViewedTurnChange(effectiveTurnIndex - 1);
    }
  };

  const handleNextTurn = () => {
    if (effectiveTurnIndex < totalTurns - 1) {
      onViewedTurnChange(effectiveTurnIndex + 1);
    } else {
      // Go to latest
      onViewedTurnChange(null);
    }
  };

  const showTurnNavigator = (
    conversationStarted &&
    (sessionActive || (!sessionActive && postSessionView === 'analytics'))
  );
  const strategyTurnResults = strategiesFlow?.turnResults || [];
  const strategyStimulusLength = strategiesFlow?.exercise?.turn_count || 0;
  const strategySubmittedTurnCount = (strategiesFlow?.conversation || []).filter(
    (turn, index) => index >= strategyStimulusLength && turn.speaker === 'counsellor',
  ).length;
  const strategyTurnCount = Math.max(strategyTurnResults.length, strategySubmittedTurnCount);
  const strategyViewedTurnIndex = strategiesFlow?.viewedTurnIndex ?? null;
  const strategySetViewedTurnIndex = strategiesFlow?.setViewedTurnIndex;
  const strategyLoading = Boolean(strategiesFlow?.loading);
  const strategyEffectiveTurnIndex = strategyViewedTurnIndex === null
    ? strategyTurnCount - 1
    : strategyViewedTurnIndex;
  const showStrategyTurnNavigator = isStrategiesMode && strategyTurnCount > 0;

  useEffect(() => {
    if (!onCompactTurnNavigatorStatusChange) {
      return undefined;
    }

    if (showStrategyTurnNavigator) {
      onCompactTurnNavigatorStatusChange({
        totalTurns: strategyTurnCount,
        effectiveTurnIndex: strategyEffectiveTurnIndex,
        isViewingLatest: (
          strategyViewedTurnIndex === null
          || strategyEffectiveTurnIndex === strategyTurnCount - 1
        ),
        showStreamingTurn: strategyLoading && strategyViewedTurnIndex === null,
        onPrevTurn: () => {
          if (strategyEffectiveTurnIndex > 0) {
            strategySetViewedTurnIndex?.(strategyEffectiveTurnIndex - 1);
          }
        },
        onNextTurn: () => {
          if (strategyEffectiveTurnIndex < strategyTurnCount - 1) {
            const nextIndex = strategyEffectiveTurnIndex + 1;
            strategySetViewedTurnIndex?.(
              nextIndex === strategyTurnCount - 1 ? null : nextIndex,
            );
          }
        },
      });
      return () => onCompactTurnNavigatorStatusChange(null);
    }

    if (!showTurnNavigator || isOarsMode) {
      onCompactTurnNavigatorStatusChange(null);
      return undefined;
    }

    onCompactTurnNavigatorStatusChange({
      totalTurns,
      effectiveTurnIndex,
      isViewingLatest,
      showStreamingTurn,
      onPrevTurn: () => {
        if (effectiveTurnIndex > 0) {
          onViewedTurnChange(effectiveTurnIndex - 1);
        }
      },
      onNextTurn: () => {
        if (effectiveTurnIndex < totalTurns - 1) {
          onViewedTurnChange(effectiveTurnIndex + 1);
        } else {
          onViewedTurnChange(null);
        }
      },
    });

    return () => onCompactTurnNavigatorStatusChange(null);
  }, [
    effectiveTurnIndex,
    isOarsMode,
    isViewingLatest,
    onCompactTurnNavigatorStatusChange,
    onViewedTurnChange,
    showStrategyTurnNavigator,
    showStreamingTurn,
    showTurnNavigator,
    strategyEffectiveTurnIndex,
    strategyLoading,
    strategySetViewedTurnIndex,
    strategyTurnCount,
    strategyViewedTurnIndex,
    totalTurns,
  ]);

  return (
    <div className={`report-panel ${collapsed ? 'collapsed' : ''}${menuOpen ? ' report-panel--menu-open' : ''}${ts3OwnsAvailableSpace ? ' report-panel--ts3-flex-layout' : ''}`}>
      {!collapsed && (
        <>
          <div className="panel-header">
            <CreditsBadge authenticated={authenticated} />
            <div className="panel-header-actions">
              <ReportHamburgerMenu
                t={t}
                menuOpen={menuOpen}
                toggleMenu={toggleMenu}
                closeMenu={closeMenu}
                menuRef={menuRef}
                menuPortalRef={menuPortalRef}
                handleThemeChange={handleThemeChange}
                theme={theme}
                onShowContacts={onShowContacts}
                onShowAccount={onShowAccount}
                onShowAbout={onShowAbout}
                onShowWorkingAlliance={onShowWorkingAlliance}
                onShowLectures={onShowLectures}
                onShowTutorial={onShowTutorial}
                onShowAutomatedMiscMiti={onShowAutomatedMiscMiti}
                onShowSimpleReflections={onShowSimpleReflections}
                onShowAffirmations={onShowAffirmations}
                onShowComplexReflections={onShowComplexReflections}
                onShowClient={onShowClient}
                onShowCounsellors={onShowCounsellors}
                guidedHighlightsEnabled={guidedHighlightsEnabled}
                onGuidedHighlightsChange={onGuidedHighlightsChange}
                skillsAnnotationsEnabled={skillsAnnotationsEnabled}
                onSkillsAnnotationsChange={onSkillsAnnotationsChange}
                suggestionsEnabled={suggestionsEnabled}
                onSuggestionsChange={onSuggestionsChange}
                language={language}
                onLanguageChange={onLanguageChange}
                authenticated={authenticated}
                onSignOut={onSignOut}
                onSignIn={requireAuth}
              />
            </div>
          </div>

          {showTs3TraversalPanel && (
            <div className={`panel-section panel-section--fixed panel-section--ts3${btcTs3Panels.ts3 ? ' panel-section--ts3-expanded' : ' panel-section--ts3-collapsed panel-section--observer-tab-collapsed'}${ts3HasRoomFromClosedAnalytics ? ' panel-section--ts3-roomy' : ''}${ts3HasRoomFromClosedWai ? ' panel-section--ts3-roomiest' : ''}`}>
              <Ts3TraversalPanel
                series={visibleAllTasksSeries.series}
                visibleCount={visibleAllTasksSeries.visibleCount}
                collapsed={!btcTs3Panels.ts3}
                onToggle={() => toggleBtcTs3Panel('ts3')}
                onSelectEntry={handleTs3EntrySelect}
                onHoverEntry={handleTs3EntryHover}
              />
            </div>
          )}

          {showWorkingAllianceChart && (
            <div ref={waiSectionRef} className={`panel-section panel-section--fixed panel-section--wai${showTs3TraversalPanel && !btcTs3Panels.wai ? ' panel-section--observer-tab-collapsed panel-section--wai-collapsed-under-ts3' : ''}${regularWorkingAllianceCollapsed ? ' panel-section--wai-tab-collapsed' : ''}`}>
              <WorkingAllianceChart
                series={visibleWorkingAllianceSeries.series}
                visibleCount={visibleWorkingAllianceSeries.visibleCount}
                collapsed={effectiveWorkingAllianceCollapsed}
                onToggle={handleWorkingAllianceToggle}
                onInfoOpen={onShowWorkingAlliance}
                resizeHeight={workingAllianceChartHeight}
                onResizeHeightChange={canResizeWorkingAlliance ? setWorkingAllianceChartHeight : undefined}
              />
              {waiIntroOpen && (
                <WorkingAllianceIntroNotice
                  anchorRef={waiSectionRef}
                  theme={theme}
                  dismissed={waiIntroDismissed}
                  onDismissedChange={handleWaiIntroDismissedChange}
                  onClose={() => setWaiIntroOpen(false)}
                />
              )}
            </div>
          )}

          <div className={`panel-content${fullSessionAnalyticsShown ? ' panel-content--analytics-live' : ''}${fullSessionAnalyticsShown ? ' panel-content--full-session-live' : ''}${showTs3TraversalPanel && !effectiveAnalyticsVisible ? ' panel-content--analytics-collapsed' : ''}${regularLiveAnalyticsCollapsed ? ' panel-content--analytics-tab-collapsed' : ''}${ts3OwnsAvailableSpace ? ' panel-content--observer-tab-collapsed' : ''}${ts3HasRoomFromClosedWai ? ' panel-content--collapsed-under-ts3-only' : ''}${postSessionMetricsBodyVisible ? ' panel-content--post-session-metrics' : ''}`}>
        <ReportOarsPanel
          t={t}
          isOarsMode={isOarsMode}
          oarsExercise={oarsExercise}
          oarsAttempt={oarsAttempt}
          oarsAnalyzing={oarsAnalyzing}
          oarsStreamingEvaluation={oarsStreamingEvaluation}
          onOarsNextExercise={onOarsNextExercise}
          onOarsTryAgain={onOarsTryAgain}
          onReturnToAccount={onReturnToAccountFromSpecificSkillReplay}
          isSpecificSkillReplay={isSpecificSkillReplay}
          onShowAutomatedMiscMitiCounsellor={onShowAutomatedMiscMitiCounsellor}
          showSpecificSkillsPostAttemptCue={showSpecificSkillsPostAttemptCue}
          notify={notify}
          MitiChipComponent={MitiChip}
          theme={theme}
          actionDockDetached
        />

        {isStrategiesMode && (
          <ReportSpecificStrategiesPanel
            t={t}
            theme={theme}
            strategiesFlow={strategiesFlow}
            MitiChipComponent={MitiChip}
            onShowAutomatedMiscMitiCounsellor={onShowAutomatedMiscMitiCounsellor}
          />
        )}

        {showBtcStage1ReadyState && (
          <div className="panel-info">
            <p className="info-text">{t('report_ready_title')}</p>
            <p className="info-subtext">{t('report_empty_no_session')}</p>
          </div>
        )}

        {showBtcStage2ReadyState && (
          clientModeCounsellorConfig ? (
            <div className="oars-context-panel btc-counsellor-context-panel">
              <div className="analytics-header">
                <h4 className="analytics-title btc-counsellor-context-title">Virtual Counsellor</h4>
                <p className="analytics-subtitle">How this counsellor will guide the conversation</p>
                <div className="btc-counsellor-pill">
                  <span className="btc-counsellor-pill__label">Selected:</span>
                  <span className="btc-counsellor-pill__value">{clientModeCounsellorConfig.label}</span>
                  <button
                    type="button"
                    className="btc-counsellor-info-btn"
                    onClick={() => onShowCounsellors && onShowCounsellors()}
                    aria-label={t('report_menu_counsellors')}
                    title={t('report_menu_counsellors')}
                  >
                    i
                  </button>
                </div>
              </div>
              <div className="oars-context-body btc-counsellor-context-body">
                <p>{clientModeCounsellorConfig.body}</p>
              </div>
            </div>
          ) : (
            <div className="panel-info">
              <p className="info-text">{t('report_ready_title')}</p>
              <p className="info-subtext">{t('report_ready_subtitle')}</p>
            </div>
          )
        )}

        {showStrategiesReadyState && (
          <div className="panel-info">
            <p className="info-text">Ready to begin</p>
            <p className="info-subtext">Start a session to see insights</p>
          </div>
        )}

        {showFreeformStage1ReadyState && (
          <div className="panel-info">
            <p className="info-text">{t('report_ready_title')}</p>
            <p className="info-subtext">{t('report_empty_no_session')}</p>
          </div>
        )}

        {showFreeformStage2ReadyState && (
          <div className="oars-context-panel oars-report-context-panel">
            <div className="analytics-header">
              <h4 className="analytics-title">Client Description</h4>
              <p className="analytics-subtitle">Background on the client for this exercise</p>
            </div>
            <div className="oars-context-body">
              <p>{fullSessionVignette?.patient_description || t('report_ready_subtitle')}</p>
            </div>
          </div>
        )}

        {!isOarsMode && sessionActive && conversationStarted && (
          <ReportFullSessionAnalytics
            t={t}
            theme={theme}
            title={t('report_in_session_title')}
            currentTurnMessages={currentTurnMessages}
            displayStageMetrics={displayStageMetrics}
            hasStageMetricsData={hasStageMetricsData}
            streamingActive={streamingActive}
            isViewingLatest={isViewingLatest}
            isClientMode={isClientMode}
            conversationStarted={conversationStarted}
            showClientParameters={isClientMode}
            revealedItems={revealedItems}
            analyticsVisible={effectiveAnalyticsVisible}
            onToggleAnalytics={handleAnalyticsToggle}
            showVisibilityToggle
            emptyScoringMessage={isClientMode ? t('report_no_data_client_mode') : t('report_no_data_yet')}
            emptyMetricsMessage={isClientMode ? t('report_no_data_client_mode') : t('report_no_data_yet')}
            AnalyticsMessageComponent={AnalyticsMessage}
            LoadingDotsComponent={LoadingDots}
            onShowAutomatedMiscMitiCounsellor={onShowAutomatedMiscMitiCounsellor}
            turnIndex={effectiveTurnIndex}
            guidanceVisible={fullSessionGuidanceVisible}
            onRevealGuidance={() => setFullSessionGuidanceVisible(true)}
            onHideGuidance={() => setFullSessionGuidanceVisible(false)}
            reportSessionId={reportSessionId}
          />
        )}

        {!isOarsMode && ((sessionId && !sessionActive) || isHistoricalReplay) && analysisStatus && (
          <div
            className={showPostSessionAnalyticsPanel
              // The review is the in-session analytics layout, not a status screen;
              // .panel-processing's status-text styles would grey out its copy.
              ? 'panel-post-session-review'
              : `panel-processing${postSessionMetricsBodyVisible ? ' panel-processing--post-session-metrics' : ''}`}
          >
            <ReportFullSessionPostSessionPanel
              t={t}
              theme={theme}
              analysisStatus={analysisStatus}
              postSessionView={postSessionView}
              setPostSessionView={setPostSessionView}
              onViewedTurnChange={onViewedTurnChange}
              currentTurnMessages={currentTurnMessages}
              displayStageMetrics={displayStageMetrics}
              hasStageMetricsData={hasStageMetricsData}
              revealedItems={revealedItems}
              systemMessagesHistory={systemMessagesHistory}
              showPostDownloadNextStepCue={showPostDownloadNextStepCue}
              showDownloadCue={showDownloadCue}
              showDownloadNotice={showDownloadNotice}
              onCloseDownloadNotice={() => setDownloadNoticeOpen(false)}
              showReturnToAccountCue={showReturnToAccountCue}
              isClientMode={isClientMode}
              onRestartSession={onRestartSession}
              showRestartAction={!isHistoricalReplay}
              onReturnToAccount={onReturnToAccount}
              onDownloadPdf={handleDownloadPdf}
              onDownloadCsv={handleDownloadCsv}
              analyticsVisible={effectiveAnalyticsVisible}
              onToggleAnalytics={handleAnalyticsToggle}
              AnalyticsMessageComponent={AnalyticsMessage}
              LoadingDotsComponent={LoadingDots}
              actionsDetached={showPostSessionMetricsPanel}
              turnIndex={effectiveTurnIndex}
              fullSessionGuidanceVisible={fullSessionGuidanceVisible}
              onRevealFullSessionGuidance={() => setFullSessionGuidanceVisible(true)}
              onHideFullSessionGuidance={() => setFullSessionGuidanceVisible(false)}
              reportSessionId={reportSessionId}
            />
          </div>
        )}
          </div>
          <OarsActionDock
            show={showOarsActionDock}
            t={t}
            onOarsTryAgain={onOarsTryAgain}
            onOarsNextExercise={onOarsNextExercise}
            onReturnToAccount={onReturnToAccountFromSpecificSkillReplay}
            isSpecificSkillReplay={isSpecificSkillReplay}
            showSpecificSkillsPostAttemptCue={showSpecificSkillsPostAttemptCue}
          />
          {showStrategyTurnNavigator && (
            <ReportTurnNavigator
              t={t}
              totalTurns={strategyTurnCount}
              effectiveTurnIndex={strategyEffectiveTurnIndex}
              isViewingLatest={strategyViewedTurnIndex === null || strategyEffectiveTurnIndex === strategyTurnCount - 1}
              showStreamingTurn={strategiesFlow?.loading && strategyViewedTurnIndex === null}
              onPrevTurn={() => {
                if (strategyEffectiveTurnIndex > 0) {
                  strategiesFlow?.setViewedTurnIndex?.(strategyEffectiveTurnIndex - 1);
                }
              }}
              onNextTurn={() => {
                if (strategyEffectiveTurnIndex < strategyTurnCount - 1) {
                  const nextIndex = strategyEffectiveTurnIndex + 1;
                  strategiesFlow?.setViewedTurnIndex?.(
                    nextIndex === strategyTurnCount - 1 ? null : nextIndex,
                  );
                }
              }}
            />
          )}
          {showStrategiesActionDock && (
            <div className="oars-action-dock">
              <div className="oars-action-bar">
                {strategiesFlow?.status === 'complete' && (
                  <button
                    type="button"
                    className={`oars-btn oars-btn--secondary${!isSpecificStrategyReplay && guidedHighlightsEnabled && strategiesCompletionCueVisible ? ' oars-btn--selection-cue' : ''}`}
                    data-guided-highlight-purpose={!isSpecificStrategyReplay && guidedHighlightsEnabled && strategiesCompletionCueVisible ? 'Try the same Scaling Questions exercise again.' : undefined}
                    onClick={onSpecificStrategyTryAgain}
                    disabled={strategiesFlow?.loading}
                  >
                    {t('report_oars_try_again')}
                  </button>
                )}
                <div className="oars-next-exercise-wrap">
                  <button
                    type="button"
                    className={`oars-btn oars-btn--primary${!isSpecificStrategyReplay && strategiesFlow?.status === 'complete' ? ' strategies-next-exercise--complete-cue' : ''}${guidedHighlightsEnabled && (isSpecificStrategyReplay || strategiesCompletionCueVisible) ? ' oars-btn--selection-cue' : ''}`}
                    data-guided-highlight-purpose={guidedHighlightsEnabled && isSpecificStrategyReplay
                      ? 'Return to your Account history.'
                      : guidedHighlightsEnabled && strategiesCompletionCueVisible
                        ? 'Start the next Scaling Questions exercise.'
                        : undefined}
                    onClick={isSpecificStrategyReplay
                      ? onReturnToAccountFromSpecificStrategyReplay
                      : () => strategiesFlow?.selectStrategy?.('Scaling Questions')}
                    disabled={strategiesFlow?.loading}
                  >
                    {isSpecificStrategyReplay ? t('account_return_to_account') : 'Next Exercise'}
                  </button>
                  {!isSpecificStrategyReplay && strategiesFlow?.status !== 'complete' && strategySubmittedTurnCount > 0 && (
                    <div className="oars-next-exercise-tooltip" role="status">
                      This exercise is still in progress. Choosing Next Exercise will skip it.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {showHistoricalMetricsActionDock && (
            <div className="oars-action-dock">
              <div className="oars-action-bar">
                <button
                  type="button"
                  className={`oars-btn oars-btn--secondary${showReturnToAccountCue ? ' oars-btn--selection-cue' : ''}`}
                  data-guided-highlight-purpose={showReturnToAccountCue ? 'Return to Account to review this saved session.' : undefined}
                  onClick={onReturnToAccount}
                >
                  {t('account_return_to_account')}
                </button>
                <div className="oars-next-exercise-wrap">
                  <button
                    type="button"
                    className="oars-btn oars-btn--primary"
                    onClick={() => setPostSessionView('analytics')}
                  >
                    {t('report_review_analytics_btn')}
                  </button>
                </div>
              </div>
            </div>
          )}
          {showLiveMetricsActionDock && (
            <div className="oars-action-dock">
              <div className="oars-action-bar">
                <button
                  type="button"
                  className={`oars-btn oars-btn--secondary${showPostDownloadNextStepCue ? ' oars-btn--selection-cue' : ''}`}
                  data-guided-highlight-purpose={showPostDownloadNextStepCue ? 'Start another Full Session with a client.' : undefined}
                  onClick={onRestartSession}
                  disabled={!liveMetricsActionsEnabled}
                >
                  {t('report_start_new_session_btn')}
                </button>
                <div className="oars-next-exercise-wrap">
                  <button
                    type="button"
                    className="oars-btn oars-btn--primary"
                    onClick={() => setPostSessionView('analytics')}
                    disabled={!liveMetricsActionsEnabled || !canReviewPostSessionAnalytics}
                  >
                    {t('report_review_analytics_btn')}
                  </button>
                </div>
              </div>
            </div>
          )}
          {showPostSessionAnalyticsPanel && (
            <div className="oars-action-dock">
              <div className="oars-action-bar">
                <div className="oars-next-exercise-wrap">
                  <button
                    type="button"
                    className="oars-btn oars-btn--primary"
                    onClick={() => {
                      setPostSessionView('metrics');
                      onViewedTurnChange(null);
                    }}
                  >
                    {t('report_back_to_metrics')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Sticky Turn Navigator at bottom - show during active session or post-session analytics review */}
      {!collapsed && showTurnNavigator && (
        <ReportTurnNavigator
          t={t}
          totalTurns={totalTurns}
          effectiveTurnIndex={effectiveTurnIndex}
          isViewingLatest={isViewingLatest}
          showStreamingTurn={showStreamingTurn}
          onPrevTurn={handlePrevTurn}
          onNextTurn={handleNextTurn}
        />
      )}
    </div>
  );
}

export default ReportPanel;
